import { lazy } from "react";
import { RouteObject } from "react-router-dom";

import PracticeBilling from "@/billing/components/PracticeBilling";
import { PracticeResults } from "@/practice-results";
import BillingSettings from "@/settings/components/BillingSettings";
import ChecklistTemplates from "@/settings/components/ChecklistTemplates";
import PracticeSettings from "@/settings/components/PracticeSettings";

const Chat = lazy(() => import("@/messages/Chat"));
const Messages = lazy(() => import("@/messages/Messages"));
const AddClientForm = lazy(() => import("@/clients/components/AddClientForm"));
const ClientList = lazy(() => import("@/clients/components/ClientList"));
const CreateConsentForm = lazy(() =>
  import("@/settings/components/Consent").then((module) => ({ default: module.CreateConsentForm })),
);
const EditAssessmentForm = lazy(() =>
  import("@/settings/components/Assessments/EditAssessmentForm").then((module) => ({
    default: module.EditAssessmentForm,
  })),
);
const EditConsentForm = lazy(() =>
  import("@/settings/components/Consent").then((module) => ({ default: module.EditConsentForm })),
);
const EditIntakeFormQuestions = lazy(
  () => import("@/settings/components/ClientIntake/EditIntakeFormQuestions"),
);
const EditIntakeFormSections = lazy(
  () => import("@/settings/components/ClientIntake/EditIntakeFormSections"),
);
const DotPhrases = lazy(() => import("@/settings/components/DotPhrases"));
const FlagLibrary = lazy(() => import("@/flags/FlagLibrary"));
const FormsLibrary = lazy(() => import("@/settings/components/FormsLibrary"));
const LabOrderTemplates = lazy(() => import("@/settings/components/LabOrderTemplates"));
const LabOrderTemplateVersionHistory = lazy(
  () => import("@/settings/components/LabOrderTemplates/LabOrderTemplateVersionHistory"),
);

const PracticeScheduleGeneral = lazy(
  () => import("@/scheduling/components/PracticeScheduleGeneral/PracticeScheduleGeneral"),
);
const ScheduleAppointmentType = lazy(
  () => import("@/scheduling/components/ScheduleAppointmentType/ScheduleAppointmentType"),
);
const PracticeSchedulesSettings = lazy(
  () => import("@/scheduling/components/PracticeScheduleModelsView/PracticeSchedulesSettings"),
);
const ScheduleProviderHours = lazy(() =>
  import("@/scheduling/components/ScheduleProviderHours").then((module) => ({
    default: module.ScheduleProviderHours,
  })),
);
const ScheduleLocationHours = lazy(
  () => import("@/scheduling/components/ScheduleLocationHours/ScheduleLocationHours"),
);
const VersionHistory = lazy(() => import("@/settings/components/VersionHistory"));
const ViewConsentForm = lazy(() =>
  import("@/settings/components/Consent").then((module) => ({ default: module.ViewConsentForm })),
);
const ViewAssessmentForm = lazy(() =>
  import("@/settings/components/Assessments/ViewAssessmentForm").then((module) => ({
    default: module.ViewAssessmentForm,
  })),
);
const ViewIntakeFormQuestions = lazy(
  () => import("@/settings/components/ClientIntake/ViewIntakeFormQuestions"),
);
const ViewIntakeFormSections = lazy(
  () => import("@/settings/components/ClientIntake/ViewIntakeFormSections"),
);
const PracticeProfile = lazy(() => import("@/settings/components/PracticeProfile/practiceProfile"));
const Teams = lazy(() => import("@/settings/components/Teams"));
const ClientExportViewerPage = lazy(
  () => import("@/client-export/components/ClientExportViewerPage"),
);
const EduResourcesPage = lazy(() =>
  import("@/edu-resources/components/EduResourcesPage").then((module) => ({
    default: module.EduResourcesPage,
  })),
);
const PracticeUsers = lazy(() => import("@/settings/components/PracticeUsers"));
const ResourceContent = lazy(() =>
  import("@/edu-resources/components/ResourceContent").then((module) => ({
    default: module.ResourceContent,
  })),
);
const FolderClientList = lazy(() =>
  import("@/edu-resources/components/FolderClientList").then((module) => ({
    default: module.FolderClientList,
  })),
);

export const PracticesRoutes: RouteObject[] = [
  {
    path: "/practices/:practiceId/clients",
    children: [
      { index: true, element: <ClientList /> },
      { path: "new", element: <AddClientForm /> },
      { path: ":clientId/export", element: <ClientExportViewerPage /> },
    ],
  },
  {
    path: "/practices/:practiceId/scheduling",
    element: <PracticeScheduleGeneral />,
  },
  {
    path: "/practices/:practiceId/billing",
    element: <PracticeBilling />,
  },
  {
    path: "/practices/:practiceId/messages",
    element: <Messages />,
    children: [
      {
        path: ":communicationId",
        element: <Chat />,
      },
    ],
  },
  {
    path: "/practices/:practiceId/settings",
    element: <PracticeSettings />,
    children: [
      {
        path: "forms",
        children: [
          {
            index: true,
            element: <FormsLibrary />,
          },
          {
            path: "consents/new",
            element: <CreateConsentForm />,
          },
          {
            path: "consents/:questionnaireName",
            element: <EditConsentForm />,
          },
          {
            path: "consents/:questionnaireName/view",
            element: <ViewConsentForm />,
          },
          {
            path: "consents/:questionnaireName/versions",
            element: <VersionHistory title="Consent Version History" />,
          },
          {
            path: "consents/:questionnaireName/versions/:version",
            element: <ViewConsentForm />,
          },
          {
            path: ":questionnaireName",
            element: <EditIntakeFormSections />,
          },
          {
            path: ":questionnaireName/view",
            element: <ViewIntakeFormSections />,
          },
          {
            path: ":questionnaireName/questions/:questionLinkId",
            element: <EditIntakeFormQuestions />,
          },
          {
            path: ":questionnaireName/questions/:questionLinkId/view",
            element: <ViewIntakeFormQuestions />,
          },
          {
            path: ":questionnaireName/versions",
            element: <VersionHistory title="Client Intake History" />,
          },
          {
            path: ":questionnaireName/versions/:version",
            element: <ViewIntakeFormSections />,
          },
          {
            path: ":questionnaireName/questions/:questionLinkId/versions/:version",
            element: <ViewIntakeFormQuestions />,
          },
          {
            path: "assessments/:assessmentName/view",
            element: <ViewAssessmentForm />,
          },
          {
            path: "assessments/:assessmentName",
            element: <EditAssessmentForm />,
          },
        ],
      },
      {
        path: "profile",
        element: <PracticeProfile />,
      },
      {
        path: "teams",
        element: <Teams />,
      },
      {
        path: "users",
        element: <PracticeUsers />,
      },
      {
        path: "scheduling",
        children: [
          {
            path: "appointment-types",
            element: <ScheduleAppointmentType />,
          },
          {
            path: "schedules",
            element: <PracticeSchedulesSettings />,
          },
          {
            path: "provider-hours",
            element: <ScheduleProviderHours />,
          },
          {
            path: "location-hours",
            element: <ScheduleLocationHours />,
          },
        ],
      },
      {
        path: "labs",
        children: [
          {
            index: true,
            element: <LabOrderTemplates />,
          },
          {
            path: ":templateId/versions",
            element: <LabOrderTemplateVersionHistory />,
          },
        ],
      },
      {
        path: "billing",
        children: [
          {
            index: true,
            element: <BillingSettings />,
          },
        ],
      },
      {
        path: "checklist",
        children: [
          {
            index: true,
            element: <ChecklistTemplates />,
          },
        ],
      },
      {
        path: "dot-phrases",
        children: [
          {
            index: true,
            element: <DotPhrases />,
          },
        ],
      },
      {
        path: "flags",
        children: [
          {
            index: true,
            element: <FlagLibrary />,
          },
        ],
      },
    ],
  },
  {
    path: "/practices/:practiceId/tests",
    children: [
      {
        index: true,
        element: <PracticeResults />,
      },
    ],
  },
  {
    path: "/practices/:practiceId/edu-resources",
    children: [
      {
        index: true,
        element: <EduResourcesPage />,
      },
      {
        path: ":resourceId",
        element: <ResourceContent />,
      },
      {
        path: "folders/:folderId/clients",
        element: <FolderClientList />,
      },
    ],
  },
];
